<template>
  <div class="quiz-market-wrapper">
    <!-- SPLASH -->
    <div v-if="showSplash" class="quiz-splash" :class="{ 'fade-out': startFading }">
      <h1>Market Quiz</h1>
      <p>
        Responde preguntas sobre el mercado eléctrico.
        Tienes 1 minuto en total y solo puedes pasar 2 preguntas.
      </p>

      <div class="rules-box">
        <div>⏱ Tiempo total: <b>60 segundos</b></div>
        <div>➡️ Preguntas que puedes pasar: <b>2 máximo</b></div>
        <div>✅ Las respuestas se validan al finalizar</div>
      </div>

      <button class="btn-start" @click="startGame">INICIAR</button>
    </div>

    <!-- HEADER -->
    <div class="quiz-header">
      <div>
        <span class="header-label">Tiempo</span>
        <strong>⏱ {{ timeLeft }}s</strong>
      </div>

      <div>
        <span class="header-label">Respondidas</span>
        <strong>{{ answeredCount }}</strong>
      </div>

      <div>
        <span class="header-label">Pasadas</span>
        <strong>{{ skippedCount }}/{{ maxSkips }}</strong>
      </div>
    </div>

    <!-- CONTENIDO -->
    <div class="quiz-container">
      <div v-if="msg" class="quiz-msg" :class="msgType">
        {{ msg }}
      </div>

      <!-- GAME OVER -->
      <div v-if="gameOver" class="quiz-overlay">
        <h2>QUIZ FINALIZADO</h2>

        <div class="final-score">
          {{ verifiedScore !== null ? verifiedScore : "..." }}
        </div>

        <div class="result-detail">
          <div v-if="correctAnswers !== null">Correctas: <b>{{ correctAnswers }}</b></div>
          <div v-if="wrongAnswers !== null">Incorrectas: <b>{{ wrongAnswers }}</b></div>
          <div>Pasadas: <b>{{ skippedCount }}</b></div>
          <div>Respondidas: <b>{{ answeredCount }}</b></div>
          <div>Total preguntas: <b>{{ questions.length }}</b></div>
        </div>

        <p v-if="verifiedScore !== null" class="verified">
          Verificado: {{ verifiedScore }} puntos
        </p>

        <p v-if="verifiedMoney !== null" class="verified">
          Dinero ganado: {{ Number(verifiedMoney).toLocaleString("es-ES") }} €
        </p>

        <button class="btn-reset" @click="resetGame">NUEVA PARTIDA</button>
      </div>

      <!-- PREGUNTA -->
      <div v-else-if="questions.length" class="question-card">
        <div class="question-progress">
          Pregunta {{ questionIndex + 1 }} / {{ questions.length }}
        </div>

        <h2 class="question-title">
          {{ currentQuestion.text }}
        </h2>

        <div class="answers-grid">
          <button
            v-for="answer in currentQuestion.answers"
            :key="answer.id"
            class="answer-btn"
            :disabled="!gameActive || answerLocked"
            :class="{
              selected: selectedAnswerId === answer.id,
              answered: answerLocked && selectedAnswerId === answer.id
            }"
            @click="selectAnswer(answer.id)"
          >
            {{ answer.text }}
          </button>
        </div>

        <div class="quiz-actions">
          <button
            class="btn-secondary"
            :disabled="!canSkip"
            @click="skipQuestion"
          >
            Pasar pregunta
          </button>

          <button
            class="btn-primary"
            :disabled="selectedAnswerId === null || answerLocked"
            @click="confirmAnswer"
          >
            Confirmar
          </button>
        </div>

        <div class="skip-info">
          Puedes pasar {{ remainingSkips }} pregunta{{ remainingSkips === 1 ? "" : "s" }} más.
        </div>
      </div>

      <div v-else class="question-card">
        <h2 class="question-title">Preparando preguntas...</h2>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";
import CryptoJS from "crypto-js";
import { useGameStore } from "@/stores/game";
import Swal from "sweetalert2";

export default {
  name: "MarketQuizGame",

  data() {
    return {
      store: useGameStore(),

      idPartida: null,
      claveTemporal: null,

      verifiedScore: null,
      verifiedMoney: null,

      showSplash: true,
      startFading: false,
      gameActive: false,
      gameOver: false,

      timerInterval: null,
      timeLeft: 60,
      totalTime: 60,

      maxSkips: 2,
      skippedCount: 0,

      questionIndex: 0,
      selectedAnswerId: null,
      answerLocked: false,

      correctAnswers: null,
      wrongAnswers: null,
      answeredCount: 0,

      msg: "",
      msgType: "",

      statsInternas: {
        startTime: null,
        totalTimeMs: 0,
      },

      questions: [],
      respuestasUsuario: [],
    };
  },

  computed: {
    currentQuestion() {
      return this.questions[this.questionIndex] || {
        id: null,
        text: "",
        answers: [],
      };
    },

    remainingSkips() {
      return Math.max(0, this.maxSkips - this.skippedCount);
    },

    canSkip() {
      return (
        this.gameActive &&
        !this.answerLocked &&
        this.remainingSkips > 0 &&
        !this.gameOver
      );
    },
  },

  beforeUnmount() {
    this.clearTimer();
  },

  methods: {
    async startGame() {
      try {
        const response = await axios.post("/api/minigames/partida/iniciar", {
          game_id: "quiz_market",
        });

        if (response.data.status === "success") {
          const payload = response.data.data || {};

          this.idPartida = payload.id_partida;
          this.claveTemporal = payload.clave_temporal;

          this.prepareGame(payload.questions || []);

          this.startFading = true;

          setTimeout(() => {
            this.showSplash = false;
            this.gameActive = true;
            this.startTimer();
          }, 400);
        }
      } catch (error) {
        console.error("Error iniciando quiz", error);

        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          "No se pudo iniciar la partida.";

        await Swal.fire({
          icon: "info",
          title: "Minijuego no disponible",
          text: msg,
          confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
        });
      }
    },

    prepareGame(questionsFromBackend) {
      this.verifiedScore = null;
      this.verifiedMoney = null;

      this.gameOver = false;
      this.gameActive = false;

      this.timeLeft = this.totalTime;
      this.skippedCount = 0;
      this.questionIndex = 0;
      this.selectedAnswerId = null;
      this.answerLocked = false;

      this.correctAnswers = null;
      this.wrongAnswers = null;
      this.answeredCount = 0;

      this.msg = "";
      this.msgType = "";

      this.respuestasUsuario = [];

      this.statsInternas = {
        startTime: Date.now(),
        totalTimeMs: 0,
      };

      this.questions = this.normalizeQuestions(questionsFromBackend);

      if (!this.questions.length) {
        throw new Error("No se han recibido preguntas para el quiz.");
      }
    },

    normalizeQuestions(rawQuestions) {
      return (rawQuestions || []).map((q) => ({
        id: Number(q.id),
        text: q.text || q.pregunta || "",
        answers: (q.answers || q.respuestas || []).map((a) => ({
          id: Number(a.id),
          text: a.text || a.respuesta || "",
        })),
      }));
    },

    startTimer() {
      this.clearTimer();

      this.timerInterval = setInterval(() => {
        if (!this.gameActive || this.gameOver) return;

        this.timeLeft--;

        if (this.timeLeft <= 0) {
          this.timeLeft = 0;
          this.endGame();
        }
      }, 1000);
    },

    clearTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },

    selectAnswer(answerId) {
      if (!this.gameActive || this.answerLocked || this.gameOver) return;
      this.selectedAnswerId = Number(answerId);
    },

    confirmAnswer() {
      if (this.selectedAnswerId === null) return;
      if (!this.gameActive || this.answerLocked || this.gameOver) return;

      this.answerLocked = true;
      this.answeredCount++;

      this.respuestasUsuario.push({
        pregunta_id: Number(this.currentQuestion.id),
        respuesta_id: Number(this.selectedAnswerId),
        pasada: false,
        tiempo_restante: Number(this.timeLeft),
      });

      this.flashMsg("Respuesta guardada", "success");

      setTimeout(() => {
        this.nextQuestion();
      }, 500);
    },

    skipQuestion() {
      if (!this.canSkip) return;

      this.answerLocked = true;
      this.skippedCount++;

      this.respuestasUsuario.push({
        pregunta_id: Number(this.currentQuestion.id),
        respuesta_id: null,
        pasada: true,
        tiempo_restante: Number(this.timeLeft),
      });

      this.flashMsg("Pregunta pasada", "info");

      setTimeout(() => {
        this.nextQuestion();
      }, 400);
    },

    nextQuestion() {
      this.selectedAnswerId = null;
      this.answerLocked = false;

      if (this.questionIndex >= this.questions.length - 1) {
        this.endGame();
        return;
      }

      this.questionIndex++;
    },

    async endGame() {
      if (this.gameOver) return;

      this.gameActive = false;
      this.gameOver = true;
      this.clearTimer();

      this.statsInternas.totalTimeMs = Date.now() - this.statsInternas.startTime;

      const stats = {
        respuestas: this.respuestasUsuario,
        pasadas: Number(this.skippedCount),
        respondidas: Number(this.answeredCount),
        total_preguntas: Number(this.questions.length),
        tiempo_restante: Number(this.timeLeft),
        tiempo_jugado_ms: Number(this.statsInternas.totalTimeMs),
      };

      await this.finalizarPartida(stats);
    },

    stableStringify(value) {
      if (value === null || typeof value !== "object") {
        return JSON.stringify(value);
      }

      if (Array.isArray(value)) {
        return `[${value.map((item) => this.stableStringify(item)).join(",")}]`;
      }

      const keys = Object.keys(value).sort();

      return `{${keys
        .map((key) => `${JSON.stringify(key)}:${this.stableStringify(value[key])}`)
        .join(",")}}`;
    },

    async finalizarPartida(stats) {
      if (!this.claveTemporal) return;

      const statsStr = this.stableStringify(stats);

      const hash = CryptoJS.HmacSHA256(statsStr, this.claveTemporal).toString(
        CryptoJS.enc.Hex
      );

      try {
        const response = await axios.post("/api/minigames/partida/finalizar", {
          id_partida: this.idPartida,
          stats,
          hash,
        });

        if (response.data.status === "success") {
          const payload = response.data.data || {};

          this.verifiedScore = payload.score;
          this.verifiedMoney = payload.dinero;

          this.correctAnswers =
            payload.correctas !== undefined ? payload.correctas : this.correctAnswers;

          this.wrongAnswers =
            payload.incorrectas !== undefined ? payload.incorrectas : this.wrongAnswers;

          await Promise.all([
            this.store.refreshStats(),
            this.store.refreshCompany(),
          ]);
        }
      } catch (error) {
        console.error("Error al validar quiz", error);

        await Swal.fire({
          icon: "error",
          title: "Error al validar",
          text:
            error?.response?.data?.message ||
            "No se pudo validar la partida.",
          confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
        });
      }
    },

    flashMsg(text, type = "info") {
      this.msg = text;
      this.msgType = type;

      setTimeout(() => {
        this.msg = "";
        this.msgType = "";
      }, 1200);
    },

    resetGame() {
      location.reload();
    },
  },
};
</script>

<style scoped>
.quiz-market-wrapper {
  --bg: #020617;
  --panel: #1e293b;
  --panel-soft: #0f172a;
  --accent: #38bdf8;
  --accent-2: #f3cd33;
  --success: #22c55e;
  --error: #ef4444;

  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  background: var(--bg);
  color: white;
  display: flex;
  flex-direction: column;
  font-family: sans-serif;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  min-height: 560px;
}

.quiz-splash {
  position: absolute;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at top, rgba(56, 189, 248, 0.25), transparent 35%),
    #020617;
  text-align: center;
  padding: 24px;
  transition: opacity 0.4s ease;
}

.quiz-splash.fade-out {
  opacity: 0;
}

.quiz-splash h1 {
  font-family: "Orbitron", sans-serif;
  font-size: 34px;
  margin-bottom: 10px;
  color: var(--accent);
}

.quiz-splash p {
  max-width: 520px;
  color: #cbd5e1;
  line-height: 1.4;
}

.rules-box {
  margin-top: 18px;
  padding: 14px 18px;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(56, 189, 248, 0.35);
  border-radius: 12px;
  text-align: left;
  color: #e2e8f0;
  font-size: 14px;
  line-height: 1.8;
}

.btn-start {
  background: var(--accent);
  color: #000;
  padding: 14px 38px;
  border-radius: 30px;
  font-weight: bold;
  border: none;
  cursor: pointer;
  margin-top: 24px;
}

.quiz-header {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 12px 0;
  background: var(--panel);
  text-align: center;
  font-weight: bold;
  border-bottom: 1px solid #334155;
}

.header-label {
  display: block;
  font-size: 11px;
  color: #94a3b8;
  font-weight: normal;
  margin-bottom: 3px;
}

.quiz-container {
  position: relative;
  flex: 1;
  min-height: 480px;
  background:
    radial-gradient(circle at center, rgba(56, 189, 248, 0.12), transparent 38%),
    #020617;
  padding: 20px;
  overflow: hidden;
}

.quiz-msg {
  position: absolute;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  padding: 8px 16px;
  border-radius: 999px;
  font-weight: bold;
  color: #020617;
  background: var(--accent);
}

.quiz-msg.success {
  background: var(--success);
  color: white;
}

.quiz-msg.error {
  background: var(--error);
  color: white;
}

.quiz-msg.info {
  background: var(--accent-2);
  color: #111827;
}

.question-card {
  width: 100%;
  max-width: 680px;
  margin: 42px auto 0;
  background: rgba(15, 23, 42, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 16px;
  padding: 22px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.35);
}

.question-progress {
  color: var(--accent);
  font-weight: bold;
  font-size: 13px;
  margin-bottom: 10px;
}

.question-title {
  font-size: 22px;
  line-height: 1.35;
  margin-bottom: 22px;
}

.answers-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.answer-btn {
  min-height: 74px;
  border: 1px solid #334155;
  background: #1e293b;
  color: white;
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  text-align: left;
  font-weight: 600;
  line-height: 1.25;
}

.answer-btn:hover:not(:disabled) {
  border-color: var(--accent);
  transform: translateY(-1px);
}

.answer-btn.selected {
  border-color: var(--accent-2);
  background: rgba(243, 205, 51, 0.16);
}

.answer-btn.answered {
  border-color: var(--success);
  background: rgba(34, 197, 94, 0.16);
}

.answer-btn:disabled {
  cursor: not-allowed;
}

.quiz-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 18px;
}

.btn-primary,
.btn-secondary,
.btn-reset {
  border: none;
  border-radius: 10px;
  padding: 12px 14px;
  font-weight: bold;
  cursor: pointer;
}

.btn-primary {
  background: var(--accent);
  color: #020617;
}

.btn-secondary {
  background: #334155;
  color: white;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.skip-info {
  margin-top: 12px;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
}

.quiz-overlay {
  position: absolute;
  inset: 0;
  background: rgba(2, 6, 23, 0.94);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 20;
  text-align: center;
  padding: 24px;
}

.quiz-overlay h2 {
  font-family: "Orbitron", sans-serif;
  color: var(--accent);
  margin-bottom: 6px;
}

.final-score {
  font-size: 54px;
  font-weight: bold;
  color: var(--accent-2);
}

.result-detail {
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid #334155;
  border-radius: 12px;
  padding: 14px 22px;
  line-height: 1.8;
  color: #e2e8f0;
}

.verified {
  color: #00ff44;
  margin: 0;
}

.btn-reset {
  background: var(--accent);
  color: #020617;
  margin-top: 10px;
}

@media (max-width: 600px) {
  .quiz-market-wrapper {
    min-height: 620px;
  }

  .quiz-header {
    font-size: 13px;
  }

  .quiz-container {
    padding: 14px;
  }

  .question-card {
    margin-top: 34px;
    padding: 16px;
  }

  .question-title {
    font-size: 18px;
  }

  .answers-grid {
    grid-template-columns: 1fr;
  }

  .answer-btn {
    min-height: 58px;
    font-size: 13px;
  }

  .quiz-actions {
    grid-template-columns: 1fr;
  }

  .quiz-splash h1 {
    font-size: 26px;
  }
}
</style>